import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { ToastService } from 'src/app/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { CommonService } from 'src/app/services/common.service';
import { GridPermissions, gridToPermissionFlags } from './gridPermissions.enum';
import { BaseGridComponent, Filter, WIDGET_CONFIG, DATE_FILTER_TYPE_ENUM, ADDITIONAL_FILTERS_QUERY_PARAM } from '../baseGrid.component';
import { ChildColumnConfig, ColumnConfig } from './columnConfig.model';
import { GridSelectionState } from './grid-selection.state';
import { AuthService } from 'src/app/services/auth.service';
import { RolePermission } from 'src/app/models/rolePermission.model';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { PermissionService } from 'src/app/services/permission.service';
import { OverviewConfig } from './overviewConfig.model';
import { Observable, Subject, filter, take } from 'rxjs';
import { ChangeDetectorRef, Component, Inject, inject, NgZone, Optional, Type } from '@angular/core';
import { RowActionMenuItem } from './rowActionMenuItem.model';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { DisplayGridColumnSettings, GridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { GridViewService } from 'src/app/services/grid-view.service';
import { Lookup } from 'src/app/models/lookup.model';
import { GridView } from 'src/app/models/gridView.model';
import { GridViewType } from 'src/app/enums/gridViewType.enum';
import { ColumnFilter, DrillDownFilterChip, QuickFilter } from 'src/app/models/columnFilter.model';
import { GridSettingsService } from './grid-settings-flyout/grid-settings.service';
import { NestedFilterService } from '../nested-filter.service';
import { GridSortSettings } from 'src/app/models/gridSortSettings.model';
import { MatPaginatorSelectConfig } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { WidgetConfig } from 'src/app/models/dashboard';
import { generateGUID } from 'src/app/_helpers/guid.helper';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { GridExport } from 'src/app/models/gridExport.model';
import { GridService } from '../grid.service';
import { ExportWizardDialogComponent } from './export-wizard-dialog/export-wizard-dialog.component';
import { AdditionalButton, BatchAction } from 'src/app/models/batchAction.model';
import { mergeSavedColumns, synthesizeDefaultView } from 'src/app/shared/grid/grid-view-default.helper';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { canonicalizeDrillDownFilterKeys, parseDrillDownFilters } from 'src/app/shared/utils/drill-down-filter-keys.util';
import { GridRowTree, GridRowTreeMeta } from './gridRowTree.model';

/**
 * Extra option rendered in the grid export menu alongside the standard Excel export. A subclass populates
 * `extraExportOptions` to contribute grid-specific exports (e.g. the samples-grid evidence pack) without a
 * new toolbar button. When the list is empty the export control stays a plain single button.
 */
export interface ExportMenuOption {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

@Component({ template: '' })
export abstract class CoreGridComponent<T> extends BaseGridComponent<T> {
  private static readonly ACTION_COLUMN_PROPERTY = 'action';
  private static readonly CHECKBOX_COLUMN_PROPERTY = '__checkbox';
  private static readonly ACTIONS_COLUMN_DISPLAY_NAME = 'Actions';
  private static readonly DEFAULT_COLUMN_TYPE = 'string';
  private static readonly DEFAULT_HEADER_CLASS = 'table-top-row left-aligned';
  private primaryRowActionFn?: (row: T) => void;
  protected hasRowClickAction = false;
  private translate = inject(TranslateService);
  private coreRouter = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  protected permissionService = inject(PermissionService);

  refreshViews$: Subject<void> = new Subject<void>();
  isFlyoutOpen: boolean = false;
  tabs: { routerLink: string; title: string; objectId?: string }[] = [];
  addEntityTitle: string;
  componentPermissions: GridPermissions;
  additionalActions: Array<{
    name: string;
    icon: string;
    action: (row: T) => void;
    hidden?: (row: T) => boolean;
    disabled?: (row: T) => boolean;
  }>;
  rowActionMenu?: (row: T) => RowActionMenuItem<T>[];
  rowActionMenuPanelClass: string = '';
  rowsWithActionMenu = new Set<T>();
  additionalButtons: AdditionalButton[] = [];
  showMenuToggle = true;
  overviewConfigs: OverviewConfig[] = [];
  protected searchPlaceholder: string;
  protected isOverviewLoading: boolean = false;
  activeFilters: Set<string> = new Set();
  deleteDisabled: boolean = false;
  deleteActionLabel: string = 'Delete';
  deleteActionIcon: string = 'delete';
  editDisabled: boolean = false;
  addDisabled: boolean = false;
  protected overviewWidth: string = '20%';
  protected showOverview: boolean = false;
  gridViewColumns: DisplayGridColumnSettings[] = [];
  gridViewTitle: string = '';
  protected extraExportOptions: ExportMenuOption[] = [];
  isGridSettingsFlyoutOpen: boolean = false;
  gridSettingsFlyoutTab: 'properties' | 'filters' | null = null;
  preSelectedFilterColumn: string | null = null;
  defaultGridViewId?: string;
  currentSelectedViewId?: string;
  gridViews: Lookup[] = [];
  private backupSelectedView: GridView | null = null;
  private columnStateAtFlyoutOpen: string | null = null;
  hasUnsavedViewChanges: boolean = false;
  private isSortUpdated: boolean = false;
  currentSort: GridSortSettings | null = null;
  filters: ColumnFilter[] = [];
  pageSizeOptions = [25, 50, 100];
  pageSize = 100;
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'core-grid-page-size-panel' };
  selectedTabIndex = 0;
  frozenColumnCount: number = 0;
  private frozenColumnLeftOffsets: Map<string, number> = new Map();
  protected selectionService: GridSelectionState<T & { id: number | string }> | null = null;
  protected get enableCheckboxSelection(): boolean { return false; }
  protected get enableNonBlankingReload(): boolean { return true; }
  protected get rowTree(): GridRowTree<T> | null { return null; }
  protected get showActionsColumn(): boolean { return true; }
  protected get showFilterControls(): boolean { return true; }
  protected toolbarComponent: Type<unknown> | null = null;
  hasRowTree = false;
  treeRows: T[] = [];
  rowTreeMeta: Record<string, GridRowTreeMeta> = {};
  treeColumnName: string | null = null;
  private treeRoots: T[] = [];
  private expandedTreeRowIds = new Set<string>();
  private knownTreeRowIds = new Set<string>();
  protected anchorColumns: string[] = [];
  protected drillDownFilterLabels: Record<string, string> = {};
  drillDownFilterChips: DrillDownFilterChip[] = [];
  protected quickFilters: QuickFilter[] = [];
  quickFilterValues: Record<string, string> = {};
  activeQuickFilters: QuickFilter[] = [];

  // Batch action toolbar
  batchActions: BatchAction[] = [];
  selectedRowsCount: number = 0;
  selectedItemsLabel: string = 'items';

  canRead: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;

  columnFrozenMeta: Record<string, { frozen: boolean; isLast: boolean; showIndicator: boolean; leftPx: string | null }> = {};
  headerClassByColumn: Record<string, string> = {};
  columnDisplayNames: Record<string, string> = {};
  columnFilterableByName: Record<string, boolean> = {};

  clearRowSelection(): void {
    if (!this.selectionService) return;
    this.selectionService.clear();
    this.onSelectionChanged();
    this.runLocalizedChangeDetection();
  }

  constructor(
    protected toastService: ToastService,
    protected dialog: MatDialog,
    protected commonService: CommonService,
    protected authService: AuthService,
    protected gridViewService: GridViewService,
    protected gridSettingsService: GridSettingsService,
    @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig
  ) {
    super(toastService, dialog, commonService, gridViewService, widgetConfig);
    if (!this.dynamicPage) {
      this.initializeGrid();
      this.tabs = this.initializeTabs();
    }
    if (!this.dynamicPage && this.objectId) {
      this.applyRolePermission();
      this.loadOverviewData();
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.detachSearchSelection();
    this.applySearchDialogLayout();
    this.subscribeToRowTree();
    this.subscribeToRowActionMenus();
  }

  private subscribeToRowActionMenus(): void {
    const menu = this.rowActionMenu;
    if (!menu) return;
    this.subscriptions.push(
      this.dataSource.entitySubject.subscribe(rows => {
        this.rowsWithActionMenu = new Set(rows.filter(row => menu(row).some(item => !item.hidden)));
      })
    );
  }

  private subscribeToRowTree(): void {
    if (!this.hasRowTree) return;
    this.subscriptions.push(
      this.dataSource.entitySubject.subscribe(rows => this.resetRowTree(rows))
    );
  }

  private resetRowTree(roots: T[]): void {
    const tree = this.rowTree;
    if (!tree) return;
    this.treeRoots = roots;
    const collect = (row: T): void => {
      const id = this.treeRowId(row);
      if (!this.knownTreeRowIds.has(id)) {
        this.knownTreeRowIds.add(id);
        if (tree.isInitiallyExpanded?.(row)) {
          this.expandedTreeRowIds.add(id);
        }
      }
      tree.getChildren(row).forEach(collect);
    };
    roots.forEach(collect);
    this.rebuildTreeRows();
  }

  private rebuildTreeRows(): void {
    const tree = this.rowTree;
    if (!tree) return;
    const rows: T[] = [];
    const meta: Record<string, GridRowTreeMeta> = {};
    const visit = (row: T, depth: number): void => {
      const id = this.treeRowId(row);
      const children = tree.getChildren(row);
      const expandable = children.length > 0;
      const expanded = expandable && this.expandedTreeRowIds.has(id);
      meta[id] = { depth, expandable, expanded };
      rows.push(row);
      if (expanded) {
        children.forEach(child => visit(child, depth + 1));
      }
    };
    this.treeRoots.forEach(root => visit(root, 0));
    this.treeRows = rows;
    this.rowTreeMeta = meta;
    this.onTreeRowsChanged(rows, meta);
  }

  protected onTreeRowsChanged(rows: T[], meta: Record<string, GridRowTreeMeta>): void { }

  private treeRowId(row: T): string {
    const id = (row as T & { id?: number | string }).id;
    if (id === undefined || id === null) {
      throw new Error('Rows of a grid with a row tree need a stable id');
    }
    return String(id);
  }

  protected toggleRowExpansion(row: T): boolean {
    const id = this.treeRowId(row);
    if (!this.rowTreeMeta[id]?.expandable) return false;
    if (this.expandedTreeRowIds.has(id)) {
      this.expandedTreeRowIds.delete(id);
    } else {
      this.expandedTreeRowIds.add(id);
    }
    this.rebuildTreeRows();
    return true;
  }

  private detachSearchSelection(): void {
    if (!this.isDialog || !Array.isArray(this.searchFieldData?.selected)) return;
    this.searchFieldData.selected = [...this.searchFieldData.selected];
  }

  private applySearchDialogLayout(): void {
    if (!this.isDialog) return;
    if (!this.columnConfigs?.some(column => column.name === CoreGridComponent.CHECKBOX_COLUMN_PROPERTY)) return;
    this.columnConfigs = this.columnConfigs.filter(column => column.name !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY);
    this.displayedColumns = this.displayedColumns?.filter(name => name !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY);
    this.computeColumnMeta();
  }

  protected get dynamicPage(): boolean {
    return false;
  }

  /**
   * Returns the grid instance identifier for user view preferences.
   * For widgets, returns 'WID_{widgetId}' to allow per-widget default views.
   * For main pages, returns objectId.
   */
  get gridId(): string {
    return this.injectedWidgetId ? `WID_${this.injectedWidgetId}` : this.objectId;
  }

  protected override getPageSize(): number {
    return this.paginator?.pageSize ?? this.pageSize;
  }

  private initializeGrid(): void {
    this.hasRowTree = !!this.rowTree;
    this.initializeProperties();
    this.initializeColumns();
    if (this.enableCheckboxSelection) {
      this.selectionService = new GridSelectionState<T & { id: number | string }>();
      if (!this.widgetMode) {
        this.prependCheckboxColumn();
      }
    }
    this.setDisplayedColumns();
    this.resolvePrimaryRowAction();
  }

  private resolvePrimaryRowAction(): void {
    this.primaryRowActionFn = this.columnConfigs?.find(column => !!column.onClick)?.onClick;
    this.hasRowClickAction = !this.widgetMode && (!!this.primaryRowActionFn || !!this.hasRowTree);
  }

  private prependCheckboxColumn(): void {
    const checkboxColumn = new ColumnConfig<T>({
      name: CoreGridComponent.CHECKBOX_COLUMN_PROPERTY,
      displayName: '',
      isSortable: false,
      cellTemplate: () => '',
      isDisplayNameConfigurable: false,
      includeInGridSettings: false,
      cssClass: () => 'checkbox-cell',
      headerCssClass: () => `${CoreGridComponent.DEFAULT_HEADER_CLASS} checkbox-cell`,
      children: () => [
        new ChildColumnConfig<T>({
          cellTemplate: () => '',
          cssClass: (row: T) => this.selectionService!.isSelected((row as T & { id: number | string }).id) ? 'checked custom-div-checkbox' : 'custom-div-checkbox',
          onClick: (row: T) => this.onSelectionChange(row)
        }),
      ],
      headerChildren: () => [
        new ChildColumnConfig<T>({
          cellTemplate: () => '',
          cssClass: () => this.selectionService!.allSelected ? 'checked custom-div-checkbox' : 'custom-div-checkbox',
          onClick: () => this.onSelectAllToggle(!this.selectionService!.allSelected)
        }),
      ]
    });
    this.columnConfigs = [checkboxColumn, ...this.columnConfigs];
  }

  protected onSelectionChange(row: T): void {
    if (!this.selectionService) return;
    this.selectionService.toggle(row as T & { id: number | string }, this.dataSource.getData() as (T & { id: number | string })[]);
    this.onSelectionChanged();
    this.runLocalizedChangeDetection();
  }

  protected onSelectAllToggle(checked: boolean): void {
    if (!this.selectionService) return;
    this.selectionService.toggleAll(checked, this.dataSource.getData() as (T & { id: number | string })[]);
    this.onSelectionChanged();
    this.runLocalizedChangeDetection();
  }

  private runLocalizedChangeDetection(): void {
    this.cdr.detach();
    this.cdr.detectChanges();
    this.ngZone.onStable.pipe(take(1)).subscribe(() => this.cdr.reattach());
  }

  /** Hook for children to react to selection changes (e.g. update buttons, sync dialog state) */
  protected onSelectionChanged(): void { }

  protected override async onGridViewInitialized(view: GridView | null): Promise<void> {
    await this.applyView(view);
  }

  private async applyView(view: GridView | null): Promise<void> {
    await this.getColumnDetailsAsync();
    this.hasUnsavedViewChanges = false;

    const hasValidColumns = view?.settingsJson?.columns && Array.isArray(view.settingsJson.columns) && view.settingsJson.columns.length > 0;

    if (view && hasValidColumns) {
      this.defaultGridViewId = view.id;
      this.currentSelectedViewId = view.id;
      this.selectedGridView = view;
      this.gridViewTitle = view.viewName;
      this.gridViewColumns = this.mapColumns(view.settingsJson.columns);
      this.currentFilters = this.gridSettingsService.loadFiltersFromSettings(view, this.gridViewColumns);
      this.activeGridFilters = Object.fromEntries(
        this.currentFilters.map(filter => [filter.column.property, true])
      );
    } else if (view) {
      const userFullName = this.authService.getCurrentUser().userFullName;
      const firstName = userFullName?.split(' ')[0] ?? 'User';

      this.gridViewColumns = this.getDefaultColumnsWithoutSettingView();
      this.gridViewTitle = view.viewName || `${firstName}'s View`;

      const serializableColumns = this.gridViewColumns
        .filter(col => col.property !== CoreGridComponent.ACTION_COLUMN_PROPERTY && col.property !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY)
        .map(col => ({ property: col.property, visible: col.visible, order: col.order }));

      this.selectedGridView = {
        ...view,
        type: this.widgetMode ? GridViewType.Dashboard : view.type,
        settingsJson: { columns: serializableColumns, filters: {} }
      };
      this.defaultGridViewId = view.id;
      this.currentSelectedViewId = view.id;
    } else {
      this.gridViewColumns = this.getDefaultColumns();
      if (this.isTemporaryView) {
        this.gridViewTitle = this.translate.instant('Temporary View');
      } else {
        const userFullName = this.authService.getCurrentUser().userFullName;
        const firstName = userFullName?.split(' ')[0] ?? 'User';
        this.gridViewTitle = `${firstName}'s View`;
      }
      this.frozenColumnCount = 0;
    }
    this.displayedColumns = this.extractDisplayedColumns(this.gridViewColumns);
    this.computeColumnMeta();
    if (view) {
      this.loadFrozenColumnsFromSettings(view);
    }
    this.setupNestedFilterRules();

    this.loadQuickFilterState();
    this.drillDownFilterChips = this.gridSettingsService.createDrillDownFilterChips(this.additionalFilters, this.drillDownFilterLabels);

    const pendingAdditionalFilters = this.isTemporaryView && Object.keys(this.additionalFilters).length > 0
      ? this.gridSettingsService.createFiltersFromAdditionalFilters(this.additionalFilters, this.gridViewColumns, this.dateFilterColumnProperty)
      : null;

    const afterOptionsLoaded = () => {
      if (pendingAdditionalFilters) {
        this.applyAdditionalFiltersAsChips(pendingAdditionalFilters);
      }
    };

    this.gridSettingsService.populateObjectOptions(this.gridViewColumns, [...this.currentFilters, ...(pendingAdditionalFilters?.filters ?? [])], afterOptionsLoaded);

    setTimeout(() => {
      this.recalculateFrozenOffsets();
    });

    if (this.hasReadPermission === null || this.hasReadPermission) {
      this.loadData();
    }

    this.recalculateFrozenOffsetsAfterLoad();
  }

  private applyAdditionalFiltersAsChips(result: { filters: ColumnFilter[], visibilityChanged: boolean }): void {
    this.currentFilters.push(...result.filters);
    result.filters.forEach(f => {
      this.activeGridFilters[f.column.property] = true;
    });
    this.filters = this.currentFilters;
    this.computeColumnMeta();

    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.filters = this.gridSettingsService.buildViewFilters(this.currentFilters, this.quickFilterValues);
    }

    const anchorChanged = this.forceAnchorColumnsVisible(this.gridViewColumns);
    if (result.visibilityChanged || anchorChanged) {
      this.displayedColumns = this.extractDisplayedColumns(this.gridViewColumns);
      this.persistColumnVisibility();
    }
  }

  private persistColumnVisibility(): void {
    if (!this.selectedGridView?.settingsJson) return;
    this.selectedGridView.settingsJson.columns = this.gridViewColumns
      .filter(col => col.property !== CoreGridComponent.ACTION_COLUMN_PROPERTY && col.property !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY)
      .map(col => ({ property: col.property, visible: col.visible, order: col.order }));
  }

  get actionColumnProperty(): string {
    return CoreGridComponent.ACTION_COLUMN_PROPERTY;
  }

  private getDefaultColumns(): DisplayGridColumnSettings[] {
    this.defaultGridViewId = undefined;
    const columns = this.buildDefaultColumns();
    if (this.isTemporaryView) {
      this.forceAnchorColumnsVisible(columns);
    }
    this.selectedGridView = this.constructDefaultView(columns);
    return columns;
  }

  private getDefaultColumnsWithoutSettingView(): DisplayGridColumnSettings[] {
    return this.buildDefaultColumns();
  }

  private forceAnchorColumnsVisible(columns: DisplayGridColumnSettings[]): boolean {
    if (!this.anchorColumns.length) return false;
    let changed = false;
    for (const col of columns) {
      if (this.anchorColumns.some(a => a.toLowerCase() === col.property.toLowerCase()) && !col.visible) {
        col.visible = true;
        changed = true;
      }
    }
    return changed;
  }

  private buildDefaultColumns(): DisplayGridColumnSettings[] {
    const safeConfigs = Array.isArray(this.columnConfigs) ? this.columnConfigs : [];
    const columns = safeConfigs.map((col, idx) => this.buildDisplayColumn(col.name, idx));
    this.ensureCheckboxColumn(columns);
    this.ensureActionsColumn(columns);
    this.solveSortableColumns();
    return columns;
  }

  private solveSortableColumns(): void {
    this.columnConfigs.forEach(column => {
      const details = this.getColumnDetails(column.name);
      column.isSortable = details?.isSortable || false;
    });
  }

  private getColumnDetails(colName: string) {
    let details = this.columnDetails[colName];
    if (!details) {
      details = Object.values(this.columnDetails).find(
        (d: any) => d.nestedPath && d.nestedPath.toLowerCase() === colName.toLowerCase()
      );
    }
    return details;
  }

  private buildDisplayColumn(property: string, order: number, visible?: boolean): DisplayGridColumnSettings {
    const details = this.getColumnDetails(property);
    const cfg = this.columnConfigs.find(c => c.name === property);
    const resolvedDisplayName = this.resolveDisplayName(property, cfg, details);
    const isActionColumn = property === CoreGridComponent.ACTION_COLUMN_PROPERTY;
    return {
      property,
      _uid: generateGUID(),
      displayName: resolvedDisplayName,
      order,
      visible: visible ?? (details?.isDefault || false),
      type: details?.type || CoreGridComponent.DEFAULT_COLUMN_TYPE,
      isNullable: details?.isNullable || false,
      disabled: isActionColumn ? true : (cfg?.includeInGridSettings === false),
      enumMap: cfg?.enumFilterMap || undefined,
      objectMap: { service: this.objectFilterMap(property) },
      valueInputTransformRule: cfg?.valueInputTransformRule,
      valueOutputTransformRule: cfg?.valueOutputTransformRule,
      isDefault: details?.isDefault || false,
      isSortable: details?.isSortable || false,
      isFilterable: details?.isFilterable || false,
      nestedPath: details?.nestedPath || null,
      isDisplayNameConfigurable: isActionColumn ? false : (cfg?.isDisplayNameConfigurable === false ? false : true),
      applyNestedFiltering: cfg?.applyNestedFiltering || false,
      hiddenFromFilter: cfg?.hiddenFromFilter || false,
      exportProperty: details?.exportProperty,
      isExportable: cfg?.isExportable ?? details?.isExportable
    };
  }

  private isDisplayGridColumnSettings(column: any): column is DisplayGridColumnSettings {
    return column && typeof column.property === 'string';
  }

  private isColumnConfig(column: any): column is ColumnConfig<T> {
    return column && typeof column.name === 'string';
  }

  private hasDisplayName(column: any): column is { displayName: string } {
    return column && typeof column.displayName === 'string';
  }

  public getColumnDisplayName(column: ColumnConfig<T> | DisplayGridColumnSettings | { property: string; displayName: string; }): string {
    let propertyName: string;

    if (this.isDisplayGridColumnSettings(column)) {
      propertyName = column.property;
    } else if (this.isColumnConfig(column)) {
      if (column.isDisplayNameConfigurable === false)
        return column.displayName ?? '';
      propertyName = column.name;
    } else {
      propertyName = (column as { property: string }).property;
    }

    if (propertyName === CoreGridComponent.ACTION_COLUMN_PROPERTY) {
      return CoreGridComponent.ACTIONS_COLUMN_DISPLAY_NAME;
    }

    const details = this.getColumnDetails(propertyName);
    if (details?.displayName) {
      return details.displayName;
    }

    if (this.hasDisplayName(column)) {
      return column.displayName;
    }

    return propertyName;
  }

  private hasActionsColumn(columns: { property: string }[]): boolean {
    return columns.some(col => col.property === CoreGridComponent.ACTION_COLUMN_PROPERTY);
  }

  private createActionsColumn(order: number) {
    return {
      property: CoreGridComponent.ACTION_COLUMN_PROPERTY,
      _uid: generateGUID(),
      displayName: CoreGridComponent.ACTIONS_COLUMN_DISPLAY_NAME,
      order: order,
      visible: true,
      type: CoreGridComponent.DEFAULT_COLUMN_TYPE,
      isNullable: false,
      disabled: true,
      enumMap: undefined,
      objectMap: { service: undefined },
      valueInputTransformRule: undefined,
      valueOutputTransformRule: undefined,
      isDefault: true,
      isSortable: false,
      isFilterable: false,
      nestedPath: null,
      isDisplayNameConfigurable: false,
      applyNestedFiltering: false,
      hiddenFromFilter: false,
      isExportable: false
    };
  }

  private ensureCheckboxColumn(columnConfigs: DisplayGridColumnSettings[]): void {
    if (!this.selectionService || this.widgetMode || this.isDialog) return;
    const existing = columnConfigs.findIndex(col => col.property === CoreGridComponent.CHECKBOX_COLUMN_PROPERTY);
    if (existing >= 0) {
      columnConfigs[existing].visible = true;
      columnConfigs[existing].order = -1;
    } else {
      columnConfigs.unshift(this.buildDisplayColumn(CoreGridComponent.CHECKBOX_COLUMN_PROPERTY, -1, true));
    }
  }

  private ensureActionsColumn(columnConfigs: DisplayGridColumnSettings[]): void {
    const existingActionsIndex = columnConfigs.findIndex(col => col.property === CoreGridComponent.ACTION_COLUMN_PROPERTY);

    if (existingActionsIndex >= 0) {
      const enhancedActionsColumn = this.createActionsColumn(columnConfigs[existingActionsIndex].order);
      if (this.widgetMode || !this.showActionsColumn) enhancedActionsColumn.visible = false;
      columnConfigs[existingActionsIndex] = enhancedActionsColumn;
    } else {
      const actionsColumn = this.createActionsColumn(columnConfigs.length);
      if (this.widgetMode || !this.showActionsColumn) actionsColumn.visible = false;
      columnConfigs.push(actionsColumn);
    }
  }

  private constructDefaultView(columns: DisplayGridColumnSettings[]): GridView {
    const serializableColumns = columns
      .filter(col => col.property !== CoreGridComponent.ACTION_COLUMN_PROPERTY && col.property !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY)
      .map(col => ({ property: col.property, visible: col.visible, order: col.order }));

    const userFullName = this.authService.getCurrentUser().userFullName;
    const firstName = userFullName?.split(' ')[0] ?? 'User';

    return synthesizeDefaultView(null, this.objectId, {
      viewName: `${firstName}'s View`,
      widgetMode: this.widgetMode,
      orgLevelId: this.getFilterBy()[this.orgFilterKey] as string,
      fallbackColumns: serializableColumns,
    });
  }

  onAddView(viewToDuplicate?: GridView | null): void {
    this.backupSelectedView = this.selectedGridView;

    const sourceColumns = viewToDuplicate
      ? viewToDuplicate.settingsJson.columns
      : this.columnConfigs.map(col => ({
        property: col.name,
        displayName: this.getColumnDisplayName(col),
        visible: true
      }));

    const freshColumns = sourceColumns.map((col, idx) => ({
      ...col,
      order: idx,
      visible: col.visible !== undefined ? col.visible : true
    }));

    if (!this.hasActionsColumn(freshColumns)) {
      freshColumns.push({
        property: CoreGridComponent.ACTION_COLUMN_PROPERTY,
        displayName: CoreGridComponent.ACTIONS_COLUMN_DISPLAY_NAME,
        order: freshColumns.length,
        visible: true
      });
    }

    this.gridViewColumns = this.mapColumns(freshColumns);

    const serializableColumns = this.gridViewColumns
      .filter(col => col.property !== CoreGridComponent.ACTION_COLUMN_PROPERTY && col.property !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY)
      .map(col => ({
        property: col.property,
        visible: col.visible,
        order: col.order
      }));

    this.selectedGridView = viewToDuplicate
      ? {
        ...viewToDuplicate,
        id: undefined,
        viewName: viewToDuplicate.viewName,
        settingsJson: { columns: serializableColumns }
      }
      : this.constructDefaultView(this.gridViewColumns);

    this.gridViewTitle = this.selectedGridView.viewName;
    this.openGridSettingsFlyout();
  }

  onViewSelected(view: Lookup, firstRender = false): void {
    if (this.isTemporaryView) {
      this.exitTemporaryView();
    }
    const discardUnsavedChanges = this.hasUnsavedViewChanges;

    // Clear selection when switching views to avoid stale state
    if (this.selectionService && this.selectionService.selectedItems.length > 0) {
      this.clearRowSelection();
    }

    if (this.selectedGridView?.id === view?.id && !discardUnsavedChanges) {
      if (this.selectedGridView?.viewName !== view.name) {
        this.selectedGridView.viewName = view.name;
        this.gridViewTitle = view.name;
      }
      return;
    }

    this.loadSelectedView(view, firstRender);
  }

  private exitTemporaryView(): void {
    this.isTemporaryView = false;
    this.additionalFilters = {};
    this.drillDownFilterChips = [];
    this.setQuickFilterValues({});
    this.coreRouter.navigate([], { queryParams: {}, queryParamsHandling: '' });
  }

  private loadSelectedView(view: Lookup, firstRender: boolean): void {
    this.currentSelectedViewId = String(view.id);
    this.gridViewTitle = view.name;

    const backupData = firstRender ? [] : this.dataSource.getData();
    if (!firstRender) {
      this.isLoading = true;
      if (!this.enableNonBlankingReload) {
        this.dataSource.updateData([]);
      }
    }

    this.gridViewService.getById(view.id).subscribe({
      next: (res) => this.applySelectedView(res, firstRender),
      error: (err) => {
        this.toastService.displayErrorMessage('Error loading selected view', true);
        if (!firstRender) {
          this.dataSource.updateData(backupData);
          this.isLoading = false;
        }
      }
    });
  }

  private applySelectedView(view: GridView, firstRender: boolean): void {
    this.hasUnsavedViewChanges = false;
    this.selectedGridView = view;
    this.currentSelectedViewId = view.id;
    this.gridViewColumns = this.mapColumns(view.settingsJson.columns);
    this.currentFilters = this.gridSettingsService.loadFiltersFromSettings(view, this.gridViewColumns);
    this.activeGridFilters = Object.fromEntries(
      this.currentFilters.map(filter => [filter.column.property, true])
    );
    this.loadQuickFilterState();
    this.displayedColumns = this.extractDisplayedColumns(this.gridViewColumns);
    this.computeColumnMeta();
    this.gridViewTitle = view.viewName;
    this.loadFrozenColumnsFromSettings(view);
    this.clearFrozenStyles();
    setTimeout(() => {
      this.recalculateFrozenOffsets();
    });
    if (!firstRender) {
      this.reloadPage();
    }
  }

  openGridSettingsFlyout(tab: 'properties' | 'filters' | null = null): void {
    this.backupSelectedView = this.selectedGridView ? { ...this.selectedGridView } : null;
    this.columnStateAtFlyoutOpen = this.columnStateKey();
    if (this.selectedGridView) {
      this.selectedGridView.organizationLevelId = this.getFilterBy()[this.orgFilterKey] as string;
    }
    this.isGridSettingsFlyoutOpen = true;
    this.gridSettingsFlyoutTab = tab;
  }

  onGridSettingsFlyoutClose(result: Lookup | null): void {
    this.isGridSettingsFlyoutOpen = false;
    this.preSelectedFilterColumn = null;
    if (result) {
      const exists = this.gridViews.some(view => view.id === result.id);

      if (!exists) {
        this.gridViews = [...this.gridViews, result];
      }

      if (this.selectedGridView && !this.selectedGridView.id && result.id) {
        this.selectedGridView.id = String(result.id);
      }

      this.currentSelectedViewId = String(result.id);
      this.gridViewTitle = result.name;

      this.displayedColumns = this.extractDisplayedColumns(
        this.mapColumns(this.selectedGridView?.settingsJson?.columns || [])
      );
      this.computeColumnMeta();

      this.frozenColumnCount = this.selectedGridView?.settingsJson?.frozenColumnCount ?? 0;

      this.refreshViews$.next();

      this.backupSelectedView = null;
      this.hasUnsavedViewChanges = false;

      if (this.isTemporaryView) {
        this.exitTemporaryView();
        this.loadSelectedView(result, false);
      }
    } else {
      const keepOnScreen = this.hasUnsavedViewChanges || this.columnStateKey() !== this.columnStateAtFlyoutOpen;
      if (this.backupSelectedView) {
        this.selectedGridView = this.backupSelectedView;
        this.currentSelectedViewId = this.backupSelectedView.id;
        this.gridViewTitle = this.backupSelectedView.viewName;
        if (!keepOnScreen) {
          this.gridViewColumns = this.mapColumns(this.backupSelectedView?.settingsJson?.columns || []);
          this.displayedColumns = this.extractDisplayedColumns(this.gridViewColumns);
          this.loadFrozenColumnsFromSettings(this.backupSelectedView);
        }
      }
      this.backupSelectedView = null;
      this.hasUnsavedViewChanges = keepOnScreen;
    }
    this.columnStateAtFlyoutOpen = null;
    this.clearFrozenStyles();
    setTimeout(() => {
      this.recalculateFrozenOffsets();
    });
  }

  private columnStateKey(): string {
    return JSON.stringify({ columns: this.displayedColumns, frozen: this.frozenColumnCount });
  }

  onViewsChanged(views: Lookup[]): void {
    this.gridViews = views;
  }

  onGridSettingsColumnsChanged(columns: DisplayGridColumnSettings[]): void {
    this.displayedColumns = this.extractDisplayedColumns(columns);
    this.computeColumnMeta();
    const maxFreezable = Math.max(0, this.displayedColumns.length - 1);

    if (this.frozenColumnCount > maxFreezable) {
      this.onFrozenColumnsChanged(maxFreezable);
    } else {
      this.clearFrozenStyles();
      setTimeout(() => {
        this.recalculateFrozenOffsets();
      });
    }
  }

  onFrozenColumnsChanged(count: number): void {
    this.frozenColumnCount = count;
    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.frozenColumnCount = count;
    }
    this.clearFrozenStyles();
    setTimeout(() => {
      this.recalculateFrozenOffsets();
    });
  }

  shouldShowNoColumnsMessage(): boolean {
    if (!this.isGridViewEnabled) return false;
    const cols = this.displayedColumns || [];
    return cols.length === 0 && !this.isLoading && this.hasPermission(GridPermissions.Read);
  }

  private mapColumns(columns: GridColumnSettings[]): DisplayGridColumnSettings[] {
    if (!columns || !Array.isArray(columns)) {
      return [];
    }
    this.solveSortableColumns();
    const availableProps = this.columnConfigs
      .map(cfg => cfg.name)
      .filter(name => name !== CoreGridComponent.ACTION_COLUMN_PROPERTY
        && name !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY);
    const mappedColumns = mergeSavedColumns(
      columns,
      availableProps,
      (property, order, visible) => this.buildDisplayColumn(property, order, visible)
    );

    this.ensureCheckboxColumn(mappedColumns);
    this.ensureActionsColumn(mappedColumns);

    return mappedColumns;
  }

  private resolveDisplayName(property: string, cfg?: ColumnConfig<T>, details?: { displayName?: string | null }): string {
    if (cfg && cfg.isDisplayNameConfigurable === false) {
      return cfg.displayName ?? '';
    }
    if (property === CoreGridComponent.ACTION_COLUMN_PROPERTY) {
      return CoreGridComponent.ACTIONS_COLUMN_DISPLAY_NAME;
    }
    if (details && details.displayName !== undefined && details.displayName !== null) {
      return details.displayName;
    }
    if (cfg && cfg.displayName !== undefined && cfg.displayName !== null) {
      return cfg.displayName;
    }
    return property;
  }

  private extractDisplayedColumns(columns: DisplayGridColumnSettings[]): string[] {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order)
      .map(col => {
        const originalProperty = col.property;
        const availableColumns = this.columnConfigs?.map(c => c.name) || [];

        if (availableColumns.includes(originalProperty)) {
          return originalProperty;
        }

        const caseInsensitiveMatch = availableColumns.find(
          col => col.toLowerCase() === originalProperty.toLowerCase()
        );

        if (caseInsensitiveMatch) {
          return caseInsensitiveMatch;
        }

        return originalProperty;
      })
      .filter(columnName => {
        const availableColumns = this.columnConfigs?.map(c => c.name) || [];
        return availableColumns.includes(columnName);
      });
  }

  private generateDefaultViewTitle(): string {
    const userFullName = this.authService.getCurrentUser().userFullName;
    const firstName = userFullName?.split(' ')[0] ?? 'User';
    const base = `${firstName}'s View`;
    let counter = 1;
    let name = `${base} ${counter}`;
    while (this.gridViews.some(v => v.name === name)) {
      name = `${base} ${++counter}`;
    }
    return name;
  }

  protected loadOverviewData(): void {
    if (!this.showOverview) {
      return;
    }
    this.isOverviewLoading = true;
    this.getOverviewData().subscribe({
      next: () => {
        this.initializeOverviewConfigs();
        this.isOverviewLoading = false;
      },
      error: () => {
        this.isOverviewLoading = false;
        this.toastService.displayErrorMessage('Error loading overview data', true);
      }
    });
  }

  protected applyRolePermission(): void {
    const rolePermission = this.permissionService.getRolePermission(this.objectId) ?? null;
    this.enforceRolePermissions(rolePermission);
  }

  protected enforceRolePermissions(rolePermission: RolePermission | null) {
    if (rolePermission) {
      this.hasReadPermission = !!(rolePermission.permissions & gridToPermissionFlags[GridPermissions.Read]);
      ([GridPermissions.Read, GridPermissions.Create, GridPermissions.Update, GridPermissions.Delete] as const).forEach(gp => {
        if (rolePermission.permissions & gridToPermissionFlags[gp]) {
          this.componentPermissions |= gp;
        }
      });
    }

    this.canRead = this.hasPermission(GridPermissions.Read);
    this.canCreate = this.hasPermission(GridPermissions.Create);
    this.canUpdate = this.hasPermission(GridPermissions.Update);
    this.canDelete = this.hasPermission(GridPermissions.Delete);

    if (this.hasReadPermission === false) {
      this.isLoading = false;
    }
  }

  toggleFilter(id: string, filterFunction: () => void): void {
    if (!this.enableNonBlankingReload) {
      this.dataSource.updateData([]);
    }
    const isFilterActive = this.activeFilters.has(id);
    this.activeFilters.clear();
    this.additionalFilters = {};
    this.drillDownFilterChips = [];
    if (this.isTemporaryView) {
      this.setQuickFilterValues({});
    }
    this.isTemporaryView = false;

    if (!isFilterActive) {
      this.activeFilters.add(id);
      filterFunction();
    }

    this.loadPage();
  }

  hasPermission(permission: GridPermissions): boolean {
    if (this.widgetMode && permission !== GridPermissions.Read) {
      return false;
    }
    return (this.componentPermissions & permission) === permission;
  }

  public trackByIndex = trackByIndex;
  public trackById = trackById;

  onFiltersChanged({ filters, changedProperty }: { filters: ColumnFilter[], changedProperty?: string }): void {
    const previousFilters = this.currentFilters;
    this.clearRemovedInheritedFilters(previousFilters, filters);
    this.syncEditedInheritedDateFilter(filters);
    this.currentFilters = filters;
    if (changedProperty) {
      this.activeGridFilters[changedProperty] ??= true;
    } else if (filters.length === 0) {
      this.deactivateFiltersFor(previousFilters);
      this.resetQuickFilters();
    }
    this.computeColumnMeta();

    const shouldReload = filters.length === 0 || (changedProperty && this.activeGridFilters[changedProperty] !== false);
    if (shouldReload) {
      this.clearRowSelection();
      this.reloadPage();
    }
  }

  private deactivateFiltersFor(removedFilters: ColumnFilter[]): void {
    for (const removed of removedFilters) {
      const property = removed.column?.property;
      if (property && this.activeGridFilters[property] !== undefined) {
        this.activeGridFilters[property] = false;
      }
    }
  }

  private syncEditedInheritedDateFilter(filters: ColumnFilter[]): void {
    const dateFilter = filters.find(f => f.inherited && f.column.property === this.dateFilterColumnProperty);
    if (!dateFilter) return;
    const value = this.gridSettingsService.buildDateFilterAdditionalValue(dateFilter);
    if (value) {
      this.additionalFilters[DATE_FILTER_TYPE_ENUM] = value;
      this.activeGridFilters[this.dateFilterColumnProperty] = true;
    }
  }

  private clearRemovedInheritedFilters(previous: ColumnFilter[], next: ColumnFilter[]): void {
    const removedInherited = previous.filter(p => p.inherited && !next.includes(p));
    for (const filter of removedInherited) {
      if (filter.column.property === this.dateFilterColumnProperty && this.additionalFilters[DATE_FILTER_TYPE_ENUM] !== undefined) {
        delete this.additionalFilters[DATE_FILTER_TYPE_ENUM];
        this.activeGridFilters[this.dateFilterColumnProperty] = false;
      }
    }
  }

  private reloadPage() {
    if (!this.enableNonBlankingReload) {
      this.dataSource.updateData([]);
    }
    this.loadPage();
    this.recalculateFrozenOffsetsAfterLoad();
  }

  protected patchRow(row: T & { id: number | string }): void {
    const data = this.dataSource.getData() as (T & { id: number | string })[];
    const index = data.findIndex(r => r.id === row.id);
    if (index === -1) {
      this.reloadPage();
      return;
    }

    const nextData = [...data];
    nextData[index] = row;
    this.dataSource.updateData(nextData);

    if (this.selectionService) {
      const selectedIndex = this.selectionService.selectedItems.findIndex(r => r.id === row.id);
      if (selectedIndex > -1) {
        const nextSelected = [...this.selectionService.selectedItems];
        nextSelected[selectedIndex] = row as T & { id: number | string };
        this.selectionService.selectedItems = nextSelected;
        this.onSelectionChanged();
      }
    }
  }

  protected removeRow(id: number | string): void {
    const data = this.dataSource.getData() as (T & { id: number | string })[];
    const index = data.findIndex(r => r.id === id);
    if (index === -1) {
      this.reloadPage();
      return;
    }

    const nextData = data.filter(r => r.id !== id);
    this.dataSource.updateData(nextData);
    this.dataSource.setCount(Math.max(0, this.dataSource.getCount() - 1));

    if (this.selectionService) {
      if (this.selectionService.isSelected(id)) {
        this.selectionService.selectedItems = this.selectionService.selectedItems.filter(r => r.id !== id);
      }
      this.selectionService.updateAllSelectedState(nextData);
      this.onSelectionChanged();
    }

    if (!nextData.length && this.paginator && this.paginator.pageIndex > 0) {
      this.paginator.pageIndex -= 1;
      this.reloadPage();
    }
  }

  removeCurrentFilter(filterToRemove: ColumnFilter): void {
    const idx = this.currentFilters.findIndex(f => f === filterToRemove);
    if (idx > -1) {
      const removedProperty = filterToRemove.column.property;
      this.activeGridFilters[removedProperty] = false;
      this.currentFilters.splice(idx, 1);

      if (this.selectedGridView?.settingsJson?.filters) {
        const filters = this.selectedGridView.settingsJson.filters;
        Object.keys(filters).forEach(key => {
          if (key.endsWith(`_${removedProperty}`) || key === removedProperty) {
            delete filters[key];
          }
        });
      }

      if (this.additionalFilters[removedProperty] !== undefined) {
        delete this.additionalFilters[removedProperty];
      }
      if (removedProperty === this.dateFilterColumnProperty && this.additionalFilters[DATE_FILTER_TYPE_ENUM] !== undefined) {
        delete this.additionalFilters[DATE_FILTER_TYPE_ENUM];
      }
      if (removedProperty === this.orgFilterKey) {
        this.dashboardOverride.clearDrillDownOverride();
      }

      this.filters = this.currentFilters;
      this.clearRowSelection();
      this.reloadPage();
    }
  }

  removeDrillDownFilter(chip: DrillDownFilterChip): void {
    delete this.additionalFilters[chip.key];
    this.drillDownFilterChips = this.drillDownFilterChips.filter(c => c !== chip);
    this.removeFiltersFromUrl([chip.key]);
    this.clearRowSelection();
    this.reloadPage();
  }

  override getFilterBy(): Filter {
    const filterBy = super.getFilterBy();
    for (const { key } of this.quickFilters) {
      delete filterBy[key];
      if (this.quickFilterValues[key] !== undefined) {
        filterBy[key] = this.quickFilterValues[key];
      }
    }
    return filterBy;
  }

  onQuickFilterToggled(key: string, isOn: boolean): void {
    const quickFilter = this.quickFilters.find(candidate => candidate.key === key);
    if (!quickFilter || isOn === (this.quickFilterValues[key] !== undefined)) return;
    const values = { ...this.quickFilterValues };
    if (isOn) {
      values[key] = quickFilter.value;
    } else {
      delete values[key];
      this.removeFiltersFromUrl([key]);
    }
    this.setQuickFilterValues(values);
    this.clearRowSelection();
    this.reloadPage();
  }

  private setQuickFilterValues(values: Record<string, string>): void {
    if (!this.quickFilters.length) return;
    this.quickFilterValues = values;
    this.activeQuickFilters = this.quickFilters.filter(quickFilter => values[quickFilter.key] !== undefined);
    if (!this.selectedGridView?.settingsJson) return;
    const viewFilters = { ...this.selectedGridView.settingsJson.filters };
    for (const { key } of this.quickFilters) {
      if (values[key] !== undefined) {
        viewFilters[key] = values[key];
      } else {
        delete viewFilters[key];
      }
    }
    this.selectedGridView.settingsJson.filters = viewFilters;
  }

  private loadQuickFilterState(): void {
    if (!this.quickFilters.length) return;
    const keys = this.quickFilters.map(quickFilter => quickFilter.key);
    if (!this.isTemporaryView) {
      const settings = this.selectedGridView?.settingsJson;
      if (settings?.filters) {
        settings.filters = canonicalizeDrillDownFilterKeys(settings.filters, keys);
      }
      this.setQuickFilterValues(this.gridSettingsService.extractQuickFilterValues(settings?.filters ?? {}, this.quickFilters));
      return;
    }
    const canonicalFilters = canonicalizeDrillDownFilterKeys(this.additionalFilters, keys);
    const taken = this.gridSettingsService.extractQuickFilterValues(canonicalFilters, this.quickFilters);
    this.setQuickFilterValues({ ...this.quickFilterValues, ...taken });
    this.additionalFilters = Object.fromEntries(
      Object.entries(canonicalFilters).filter(([key]) => !Object.hasOwn(taken, key))
    );
  }

  private resetQuickFilters(): void {
    if (!this.quickFilters.length) return;
    this.removeFiltersFromUrl(this.quickFilters.map(quickFilter => quickFilter.key));
    this.setQuickFilterValues({});
  }

  private removeFiltersFromUrl(keys: string[]): void {
    const urlFilters = parseDrillDownFilters(this.coreRouter.parseUrl(this.coreRouter.url).queryParams[ADDITIONAL_FILTERS_QUERY_PARAM]);
    if (!urlFilters) return;
    const remaining = canonicalizeDrillDownFilterKeys(urlFilters, keys);
    const presentKeys = keys.filter(key => key in remaining);
    if (!presentKeys.length) return;
    presentKeys.forEach(key => delete remaining[key]);
    this.coreRouter.navigate([], {
      queryParams: { [ADDITIONAL_FILTERS_QUERY_PARAM]: Object.keys(remaining).length ? JSON.stringify(remaining) : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onFilterChipClick(filter: ColumnFilter): void {
    const key = filter.column.property;
    if (this.activeGridFilters[key] === undefined) {
      this.activeGridFilters[key] = false;
    } else {
      this.activeGridFilters[key] = !this.activeGridFilters[key];
    }
    if (key === this.orgFilterKey) {
      if (this.activeGridFilters[key]) {
        this.applyDrillDownOverrideFromAdditionalFilters();
      } else {
        this.dashboardOverride.clearDrillDownOverride();
      }
    }
    this.clearRowSelection();
    this.reloadPage();
  }

  onSortChangedFromTable(event: Sort): void {
    if (this.isSortUpdated) return;

    const newSort: GridSortSettings = {
      sortBy: event.active,
      sortOrder: event.direction || 'asc',
    };

    if (this.sortEquals(this.currentSort, newSort)) return;

    this.currentSort = { ...newSort };
    this.sortActive = newSort.sortBy;
    this.defaultOrderBy = newSort.sortOrder;

    if (this.sort) {
      this.sort.active = newSort.sortBy;
      this.sort.direction = newSort.sortOrder as SortDirection;
    }

    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.sort = { ...newSort };
    }

    this.reloadPage();
  }

  onSortChangedFromFlyout(sort: GridSortSettings | null): void {
    this.currentSort = sort ? { ...sort } : null;
    this.sortActive = sort?.sortBy || '';
    this.defaultOrderBy = sort?.sortOrder || 'asc';

    if (this.selectedGridView?.settingsJson) {
      this.selectedGridView.settingsJson.sort = sort ? { ...sort } : undefined;
    }

    this.isSortUpdated = true;
    if (this.sort && sort) {
      this.sort.active = sort.sortBy;
      this.sort.direction = sort.sortOrder as 'asc' | 'desc';
    }
    this.isSortUpdated = false;

    if (!sort) this.clearMatSort();
    this.reloadPage();
  }

  private sortEquals(a: GridSortSettings | null, b: GridSortSettings | null): boolean {
    return a?.sortBy === b?.sortBy && a?.sortOrder === b?.sortOrder;
  }

  protected objectFilterMap(propertyName: string): any | undefined {
    return undefined;
  }

  protected enumToValueNameArray(enumObject: any): { value: number, name: string }[] {
    return this.commonService.getEnumNameListArray(enumObject).map((e: any) => ({
      value: e.id,
      name: e.value
    }));
  }

  protected setDisplayedColumns(): void {
    this.displayedColumns = this.columnConfigs
      .map((x) => x.name)
      .filter((name) => this.showActionsColumn || name !== CoreGridComponent.ACTION_COLUMN_PROPERTY);
    this.computeColumnMeta();
  }

  exportToExcel(): void {
    const orderBy = this.sortActive || this.getDefaultOrderByColumn();
    const sortDirection = this.defaultOrderBy || 'asc';

    const currentSort = orderBy ? {
      sortBy: orderBy,
      sortOrder: sortDirection
    } : null;

    const filterByJson = JSON.stringify(this.getFilterBy());
    const searchFilter = this.searchField?.nativeElement?.value || '';

    const currentUser = this.authService.getCurrentUser();
    const datePreference = currentUser?.preferences?.datePreference ?? 0;
    const timePreference = currentUser?.preferences?.timePreference ?? 0;

    const dialogRef = this.dialog.open(ExportWizardDialogComponent, {
      width: '700px',
      maxHeight: '80vh',
      data: {
        viewName: this.gridViewTitle || this.objectId,
        allColumns: this.gridViewColumns,
        currentSort: currentSort,
        filter: searchFilter,
        filterBy: filterByJson,
        dateFormat: datePreference,
        timeFormat: timePreference
      }
    });

    dialogRef.afterClosed().subscribe((result: GridExport | null) => {
      if (result) {
        this.executeExport(result);
      }
    });
  }

  private executeExport(request: GridExport): void {
    const service = this.gridService as GridService<T>;
    if (!service?.export) {
      this.toastService.displayErrorMessage('Export not supported for this grid', true);
      return;
    }

    this.toastService.showInfo('Preparing export...', true);

    service.export(request, this.getExportHeaders()).subscribe({
      next: (response: HttpResponse<Blob>) => {
        if (this.saveBlobResponse(response, `${request.viewName}_export.xlsx`)) {
          this.toastService.showSuccess('Export completed', true);
        }
      },
      error: (err: Error) => {
        this.toastService.displayErrorMessage('Export failed', true);
      }
    });
  }

  /**
   * Saves a streamed blob response, reading the filename from Content-Disposition (falling back to
   * `defaultFilename`). Returns false and toasts when the response carried no body. Shared by the standard
   * Excel export and grid-specific exports (e.g. the samples-grid evidence pack).
   */
  protected saveBlobResponse(response: HttpResponse<Blob>, defaultFilename: string): boolean {
    const blob = response.body;
    if (!blob) {
      this.toastService.displayErrorMessage('Export failed - no data', true);
      return false;
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = defaultFilename;

    if (contentDisposition) {
      // NOTE: this is RegExp.prototype.exec parsing an HTTP response header string — not
      // child_process.exec/eval. No command or code is executed here.
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        filename = this.sanitizeDownloadFilename(matches[1].replace(/['"]/g, ''), defaultFilename);
      }
    }

    this.downloadExcelFile(blob, filename);
    return true;
  }

  /**
   * Reduces a filename parsed from a Content-Disposition header to a safe download basename: strips any
   * path components (`/`, `\`), removes control characters, and falls back to `defaultFilename` when the
   * result is empty. Defence-in-depth so a crafted header value can only ever name the download.
   */
  private sanitizeDownloadFilename(candidate: string, defaultFilename: string): string {
    // Drop control characters (code point < 32 or DEL) without a control-range regex literal.
    const printable = Array.from(candidate)
      .filter(ch => {
        const code = ch.charCodeAt(0);
        return code >= 32 && code !== 127;
      })
      .join('');
    // Keep only the final path segment so a crafted header cannot inject directory components.
    const basename = printable.split(/[\\/]/).pop()?.trim();
    return basename ? basename : defaultFilename;
  }

  /**
   * Builds a {@link GridExport} carrying the current grid scope (view name, visible columns, sort, search
   * and filterBy, plus the user's date/time preferences) without opening the export wizard. Grid-specific
   * exports (e.g. the samples-grid evidence pack) reuse this so they cover the exact same secure set as the
   * Excel export.
   */
  protected buildExportScope(): GridExport {
    const orderBy = this.sortActive || this.getDefaultOrderByColumn();
    const sortDirection = this.defaultOrderBy || 'asc';
    const currentSort: GridSortSettings | undefined = orderBy ? { sortBy: orderBy, sortOrder: sortDirection as 'asc' | 'desc' } : undefined;
    const currentUser = this.authService.getCurrentUser();

    return {
      viewName: this.gridViewTitle || this.objectId,
      columns: (this.gridViewColumns ?? []).map((column, index) => ({
        property: column.exportProperty ?? column.property,
        displayName: column.displayName,
        visible: true,
        order: index
      })),
      sort: currentSort,
      filter: this.searchField?.nativeElement?.value || '',
      filterBy: JSON.stringify(this.getFilterBy()),
      dateFormat: currentUser?.preferences?.datePreference ?? 0,
      timeFormat: currentUser?.preferences?.timePreference ?? 0
    };
  }

  protected getExportHeaders(): HttpHeaders | undefined {
    return undefined;
  }

  private downloadExcelFile(blob: Blob, filename: string): void {
    // Delegates to the shared helper, which defers URL.revokeObjectURL so the
    // browser can finish reading the blob (prevents 0-byte/corrupted exports).
    downloadBlob(blob, filename);
  }

  protected setOverviewWidthPercentage(value: number): void {
    if (value < 20 || value > 80) {
      throw new Error(`Invalid overview width: ${value}%. It must be between 20% and 80%.`);
    }
    this.overviewWidth = `${value}%`;
  }

  protected flyout?: {
    open: <TComponent>(component: Type<TComponent>, data?: any, width?: string, closesOnOutsideClick?: boolean) => void;
    close: () => void;
  };

  protected openFlyoutInternal<TComponent>(component: Type<TComponent>, data?: any, width: string = '400px', closesOnOutsideClick: boolean = true): void {
    if (this.flyout) {
      this.flyout.open(component, data, width, closesOnOutsideClick);
      this.isFlyoutOpen = true;
      this.onFlyoutOpen();
    }
  }

  protected closeFlyoutInternal(): void {
    if (this.flyout?.close) {
      this.flyout.close();
      this.isFlyoutOpen = false;
      this.onFlyoutClose();
    }
  }

  protected onFlyoutOpen(): void {
    return;
  }

  protected onFlyoutClose(): void {
    return;
  }

  protected override setupNestedFilterRules(): void {
    if (this.gridViewColumns && this.gridViewColumns.length > 0) {
      const rules = NestedFilterService.getInstance().createNestedFilterRules(this.columnDetails, this.gridViewColumns);
      this.dataSource.setNestedFilterRules(rules);
    }
  }

  isColumnFilterable(column: ColumnConfig<T>): boolean {
    const details = this.getColumnDetails(column.name);
    return details?.isFilterable === true;
  }

  openFilterForColumn(columnName: string): void {
    this.preSelectedFilterColumn = columnName;
    this.openGridSettingsFlyout('filters');
  }

  onRowClicked(row: T & { id: number | string }): void {
    if (this.isDialog) {
      if (this.searchFieldData.isMultiple) {
        if (!Array.isArray(this.searchFieldData.selected)) { this.searchFieldData.selected = []; }

        const selected = this.searchFieldData.selected as (number | string)[];
        const index = selected.indexOf(row.id);

        if (index === -1) {
          selected.push(row.id);
          this.selectedSearchItems.push(row);
        } else {
          selected.splice(index, 1);
          const itemIndex = this.selectedSearchItems.findIndex(item => (item as T & { id: number | string }).id === row.id);
          if (itemIndex > -1) this.selectedSearchItems.splice(itemIndex, 1);
        }
      } else {
        this.closeSearch(row.id, row);
      }
      return;
    }

    if (this.widgetMode || this.hasTextSelection()) return;
    if (this.hasRowTree && this.toggleRowExpansion(row)) return;
    this.primaryRowActionFn?.(row);
  }

  onCellClicked(column: ColumnConfig<T>, row: T, event: Event): void {
    if (this.isDialog) return;
    if (column.name === CoreGridComponent.CHECKBOX_COLUMN_PROPERTY) {
      event.stopPropagation();
      this.onSelectionChange(row);
      return;
    }
    if (!column.onClick) return;
    event.stopPropagation();
    column.onClick(row);
  }

  onHeaderCellClicked(column: ColumnConfig<T>): void {
    if (column.name !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY || !this.selectionService) return;
    this.onSelectAllToggle(!this.selectionService.allSelected);
  }

  onChildClicked(child: ChildColumnConfig<T>, row: T, event: Event): void {
    if (child.readonly && child.readonly(row)) return;
    if (!child.onClick) return;
    event.stopPropagation();
    child.onClick(row);
  }

  private hasTextSelection(): boolean {
    const selection = window.getSelection();
    return !!selection && selection.toString().length > 0;
  }

  closeSearch(value: boolean | number | string | (number | string)[], items?: T | T[]) {
    if (typeof value === 'boolean') {
      this.searchDialogRef.close({ value: true, items: null });
      return;
    }
    this.searchDialogRef.close({ value, items });
  }

  isSearchRowSelected(row: any): boolean {
    const selected = this.searchFieldData.selected;

    if (this.searchFieldData.isMultiple) {
      return Array.isArray(selected) && selected.includes(row.id);
    }

    return selected === row.id;
  }

  private getEffectiveFrozenColumnCount(): number {
    if (this.frozenColumnCount === 0) return 0;
    let configurableCount = 0;
    for (let i = 0; i < this.displayedColumns.length; i++) {
      const cfg = this.columnConfigs?.find(c => c.name === this.displayedColumns[i]);
      const isNonConfigurable = cfg?.includeInGridSettings === false || cfg?.isDisplayNameConfigurable === false;
      if (!isNonConfigurable) {
        configurableCount++;
      }
      if (configurableCount >= this.frozenColumnCount) {
        return i + 1;
      }
    }
    return Math.min(this.displayedColumns.length, this.displayedColumns.length - 1);
  }

  shouldShowFrozenIndicator(columnName: string): boolean {
    if (!this.isColumnFrozenInGrid(columnName)) return false;
    const cfg = this.columnConfigs?.find(c => c.name === columnName);
    return !(cfg?.includeInGridSettings === false || cfg?.isDisplayNameConfigurable === false);
  }

  isColumnFrozenInGrid(columnName: string): boolean {
    const effectiveCount = this.getEffectiveFrozenColumnCount();
    if (effectiveCount === 0) return false;
    const index = this.displayedColumns.indexOf(columnName);
    return index >= 0 && index < effectiveCount;
  }

  isLastFrozenColumn(columnName: string): boolean {
    const effectiveCount = this.getEffectiveFrozenColumnCount();
    if (effectiveCount === 0) return false;
    return this.displayedColumns[effectiveCount - 1] === columnName;
  }

  getFrozenColumnLeft(columnName: string): string | null {
    if (!this.isColumnFrozenInGrid(columnName)) return null;
    const offset = this.frozenColumnLeftOffsets.get(columnName);
    return offset != null ? `${offset}px` : '0px';
  }

  getHeaderCellClass(column: ColumnConfig<T>): string {
    const baseClass = column.headerCssClass ? column.headerCssClass(undefined as unknown as T) : CoreGridComponent.DEFAULT_HEADER_CLASS;
    return baseClass;
  }

  getDataCellClass(column: ColumnConfig<T>, row: T): string {
    const baseClass = column.cssClass ? column.cssClass(row) + ' left-aligned' : 'left-aligned';
    return baseClass;
  }

  private clearFrozenStyles(): void {
    const tableEl = document.querySelector(`table[data-grid-id="${this.gridId}"]`);
    if (!tableEl) return;

    const allCells = tableEl.querySelectorAll<HTMLElement>('th.frozen-column, td.frozen-column');
    allCells.forEach((cell) => {
      cell.style.left = '';
    });
  }

  private loadFrozenColumnsFromSettings(view: GridView): void {
    this.frozenColumnCount = view.settingsJson?.frozenColumnCount ?? 0;
    const maxFreezable = Math.max(0, this.displayedColumns.length - 1);

    if (this.frozenColumnCount > maxFreezable) {
      this.frozenColumnCount = maxFreezable;
    }
  }

  private recalculateFrozenOffsetsAfterLoad(): void {
    if (this.frozenColumnCount > 0) {
      this.dataSource.isLoading$.pipe(
        filter(loading => !loading),
        take(1)
      ).subscribe(() => {
        setTimeout(() => this.recalculateFrozenOffsets());
      });
    }
  }

  private recalculateFrozenOffsets(): void {
    this.frozenColumnLeftOffsets.clear();
    const effectiveCount = this.getEffectiveFrozenColumnCount();
    if (effectiveCount === 0) return;

    const tableEl = document.querySelector(`table[data-grid-id="${this.gridId}"]`);
    if (!tableEl) return;

    const headerRow = tableEl.querySelector('thead tr');
    if (!headerRow) return;

    const frozenCols = this.displayedColumns.slice(0, effectiveCount);
    let cumulativeLeft = 0;

    for (const colName of frozenCols) {
      const th = headerRow.querySelector(`th[data-col-name="${colName}"]`) as HTMLElement;
      if (th) {
        this.frozenColumnLeftOffsets.set(colName, cumulativeLeft);
        cumulativeLeft += th.offsetWidth;
      }
    }

    this.frozenColumnLeftOffsets.forEach((leftVal, colName) => {
      const cells = tableEl.querySelectorAll<HTMLElement>(`[data-col-name="${colName}"].frozen-column`);
      cells.forEach((cell) => {
        cell.style.left = `${leftVal}px`;
      });
    });

    this.computeFrozenColumnMeta();
  }

  private computeFrozenColumnMeta(): void {
    const meta: Record<string, { frozen: boolean; isLast: boolean; showIndicator: boolean; leftPx: string | null }> = {};
    const effectiveCount = this.getEffectiveFrozenColumnCount();
    for (const colName of this.displayedColumns) {
      const frozen = effectiveCount > 0 && this.displayedColumns.indexOf(colName) < effectiveCount;
      const isLast = effectiveCount > 0 && this.displayedColumns[effectiveCount - 1] === colName;
      const cfg = this.columnConfigs?.find(c => c.name === colName);
      const showIndicator = frozen && !(cfg?.includeInGridSettings === false || cfg?.isDisplayNameConfigurable === false);
      const offset = this.frozenColumnLeftOffsets.get(colName);
      const leftPx = frozen ? (offset != null ? `${offset}px` : '0px') : null;
      meta[colName] = { frozen, isLast, showIndicator, leftPx };
    }
    this.columnFrozenMeta = meta;
  }

  private computeColumnMeta(): void {
    const headerClass: Record<string, string> = {};
    const displayNames: Record<string, string> = {};
    const filterable: Record<string, boolean> = {};
    for (const col of this.columnConfigs) {
      headerClass[col.name] = col.headerCssClass ? col.headerCssClass(undefined as unknown as T) : CoreGridComponent.DEFAULT_HEADER_CLASS;
      displayNames[col.name] = this.getColumnDisplayName(col);
      filterable[col.name] = this.isColumnFilterable(col);
    }
    for (const filter of (this.currentFilters || [])) {
      displayNames[filter.column.property] = this.getColumnDisplayName(filter.column);
    }
    if (this.hasRowTree) {
      this.treeColumnName = this.displayedColumns?.find(name =>
        name !== CoreGridComponent.ACTION_COLUMN_PROPERTY && name !== CoreGridComponent.CHECKBOX_COLUMN_PROPERTY
      ) ?? null;
    }
    this.headerClassByColumn = headerClass;
    this.columnDisplayNames = displayNames;
    this.columnFilterableByName = filterable;
  }

  onAdditionalActionClicked(
    action: { action: (row: T) => void; disabled?: (row: T) => boolean },
    row: T,
    event: Event
  ): void {
    event.stopPropagation();
    if (action.disabled && action.disabled(row)) return;
    action.action(row);
  }

  abstract initializeProperties(): void;

  abstract initializeColumns(): void;

  abstract openEntityDialog(entityId?: string): void;

  abstract openEntityDeleteDialog(entity: T): void;

  abstract initializeOverviewConfigs(): void;

  abstract initializeTabs(): { routerLink: string; title: string; objectId?: string }[];

  abstract getOverviewData(): Observable<void>;
}
